import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import UserAddressService from "../../services/useraddress/useraddress.service";
import DistrictDataTable from "./district-datatable";
import { DistrictDTOModel } from "../../models/cust-address/cust-address-model";
import AppLoading from '../app-loading/app-loading';
import DistrictSearch from "./district-search";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

const AddDistrict: React.FC = () => {
  const { t } = useTranslation();
  const [districtList, setDistrictList] = useState<DistrictDTOModel[]>([]); 
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);  
  const [pageNumber, setPageNumber] = useState<number>(1);  
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedDistrict, setSelectedDistrict] = useState<DistrictDTOModel | null>(null);
  const toast = useRef<Toast>(null);
  
  const showMessage = (severity: 'success' | 'error'|'warn', summary: string, detail: string) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const fetchData = async (pageNum: number, pageSize: number, search: string = '') => {
    setLoading(true);
    try {
      const response = search 
        ? await UserAddressService.keywordSearchFilter(search, pageNum, pageSize)
        : await UserAddressService.getDistricts(pageNum, pageSize);
      
      setDistrictList(response.districts); 
      setTotalRecords(response.totalRecords);  
    } catch (error) {
      console.error("Error fetching data:", error);
      setDistrictList([]);  
      showMessage('error', 'Error', 'Failed to fetch districts');
    } finally {
      setLoading(false);
    }
  };

  const reloadPage = (val: boolean) => {
    if (val) {
      fetchData(pageNumber, pageSize, searchTerm);
    }
  };

  const handlePageChange = async (event: any) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    const newPageSize = event.rows;
    
    setPageNumber(newPageNumber);
    setPageSize(newPageSize);
    fetchData(newPageNumber, newPageSize, searchTerm);
  };

  const handleDistrictUpdate = async (districtId: number, newName: string) => {
    if (!newName.trim()) {
      showMessage('warn', 'Validation Warning', 'District name cannot be empty');
      return false;
    }
 
    try {
      await UserAddressService.updatedistrict(districtId, newName.trim());
      setDistrictList(prev => prev.map(d => d.districtId === districtId ? {...d, districtName: newName.trim()} : d));
      await fetchData(pageNumber, pageSize, searchTerm);
      showMessage('success', 'Success', 'District updated successfully');
      return true;
    } catch (error) {
      showMessage('error', 'Error', error instanceof Error ? error.message : 'Failed to update district');
      await fetchData(pageNumber, pageSize, searchTerm);
      return false;
    }
  };
 

  const handleDistrictDelete = async () => {
    if (!selectedDistrict) return;
    
    setLoading(true);
    try {
      const response = await UserAddressService.deleteDistrict(selectedDistrict.districtId);
      if (response.isSuccess) {
        showMessage('success', 'Success', response.message || 'District deleted successfully');
        fetchData(pageNumber, pageSize, searchTerm); 
      } else {
        showMessage('error', 'Error', response.message || 'Failed to delete district');
      }
    } catch (error) {
      console.error("Error deleting district:", error);
      showMessage('error', 'Error', 'Failed to delete district');
    } finally {
      setLoading(false);
      setShowDeleteDialog(false);
      setSelectedDistrict(null);
    }
  };

  const confirmDelete = (district: DistrictDTOModel) => {
    setSelectedDistrict(district);
    setShowDeleteDialog(true);
  };

  useEffect(() => {
    fetchData(pageNumber, pageSize, searchTerm);
  }, [pageNumber, pageSize, searchTerm]);

  return (
    <>
      <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
        <div className="col-12">
          <h5 className="ml-1 mb-0">{"District"}</h5>
        </div>

        <DistrictSearch
          reloadPage={reloadPage}
          setLoading={setLoading}
          pageNumber={pageNumber}
          pageSize={pageSize}
          setPageNumber={setPageNumber}
          setPageSize={setPageSize}
          setDistrictList={setDistrictList}  
          setTotalRecords={setTotalRecords}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
        />

        <DistrictDataTable
          data={districtList}  
          NoofRecords={totalRecords}
          pageNumber={pageNumber}
          pageSize={pageSize}
          searchTerm={searchTerm}
          onPageChange={handlePageChange}
          onUpdateDistrict={handleDistrictUpdate}
          onDeleteDistrict={confirmDelete}
        />
      </div>
     
      <Toast ref={toast} position="top-right" />
      {loading && <AppLoading />}

      <Dialog
        visible={showDeleteDialog}
        style={{ width: '450px' }}
        header="Confirm Delete"
        modal
        onHide={() => setShowDeleteDialog(false)}
        footer={
          <div>
            <Button
              label="No"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowDeleteDialog(false)}
              disabled={loading}
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              className="p-button-danger"
              onClick={handleDistrictDelete}
              loading={loading}
            />
          </div>
        }
      >
        <div className="confirmation-content">
          {selectedDistrict && (
            <span>
              Are you sure you want to delete <strong>{selectedDistrict.districtName}</strong>?
            </span>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default AddDistrict;
